/**
 * TOOL_CONTENT — binds each tool-kind WindowId to its content component.
 *
 * The tool half of the engine's "what each window renders" layer (registry.ts
 * = metadata, WindowManagerContext = runtime state, this = id→component for
 * `kind: 'tool'`). Ported from the kit's old `ui/debug/content.tsx`
 * CONTENT_BY_ID — only the import paths are re-pointed (the tool components
 * themselves stay in place under HUD/, netobs/, debug/toolbox/, Windows/).
 *
 * Merged into the exhaustive `Record<WindowId, _>` by content.ts; that merge
 * is the tsc-level completeness drift guard once gameplay/diagnostic maps land.
 */

import type { WindowContentMap } from './types';
import { LatencyOverlayContent } from '../HUD/LatencyOverlay';
import { NetTrafficOverlayContent } from '../HUD/NetTrafficOverlay';
import { NetobsPacketSparklineContent } from '../netobs/NetobsPacketSparkline';
import { NetSpikeAnalyzerContent } from '../HUD/NetSpikeAnalyzer';
import { NetcodeAllocPanel } from '../DiagnosticHub/components/NetcodeAllocPanel';
import { NetobsTickBadgeContent } from '../netobs/NetobsTickBadge';
import { DevStatusBarContent } from '../components/DevStatusBar';
import { DebugInfoOverlayContent } from '../debug/toolbox/DebugInfoOverlay';
import { TeleportTestBarContent } from '../debug/toolbox/TeleportTestBar';
import { EventRateOverlayContent } from '../debug/toolbox/EventRateOverlay';
import { LagRootCauseOverlayContent } from '../debug/toolbox/LagRootCauseOverlay';
import { PositionTrackerOverlayContent } from '../debug/toolbox/PositionTrackerOverlay';
import { FrameJitterOverlayContent } from '../debug/toolbox/FrameJitterOverlay';
import { SmoothnessDashboardOverlayContent } from '../debug/toolbox/SmoothnessDashboardOverlay';
import { PosDriftOverlayContent } from '../debug/toolbox/PosDriftOverlay';
import { InterpTuningOverlayContent } from '../debug/toolbox/InterpTuningOverlay';
import { SystemHealthPanel } from '../Windows/DevTools/SystemHealthPanel';
import { SceneFxTunerPanel } from '../Windows/DevTools/SceneFxTunerPanel';
import { LevelDiagnosticsPanel } from '../panels/LevelDiagnosticsPanel';
import { PerfGraphOverlayContent } from '../debug/toolbox/PerfGraphOverlay';
import { PhysicsSafetyOverlayContent } from '../debug/toolbox/PhysicsSafetyOverlay';
import { MultiplayerDiagnosticsPanelContent } from '../debug/toolbox/MultiplayerDiagnosticsPanel';
import { PhysicsDiagnosticPanelContent } from '../HUD/PhysicsDiagnosticPanel';
import { RenderingDiagnosticPanelContent } from '../HUD/RenderingDiagnosticPanel';
import { NetobsAuthorityLegendContent } from '../netobs/NetobsAuthorityLegend';
import { ProjectileTrackerPanelContent } from '../panels/ProjectileTrackerPanel';

/** Tool-kind id→component map. Keyed by WindowId (partial — only the 25 tool
 *  ids today); content.ts merges the per-kind maps into the exhaustive
 *  `Record<WindowId, React.FC>` (the tsc completeness guard) and the runtime
 *  completeness test pins registry↔content parity. */
export const TOOL_CONTENT: WindowContentMap = {
  latency: LatencyOverlayContent,
  netTraffic: NetTrafficOverlayContent,
  netobsSparkline: NetobsPacketSparklineContent,
  netSpike: NetSpikeAnalyzerContent,
  netcodeAlloc: NetcodeAllocPanel,
  tickBadge: NetobsTickBadgeContent,
  statusBar: DevStatusBarContent,
  debugInfo: DebugInfoOverlayContent,
  teleport: TeleportTestBarContent,
  eventRate: EventRateOverlayContent,
  lagRootCause: LagRootCauseOverlayContent,
  positionTracker: PositionTrackerOverlayContent,
  frameJitter: FrameJitterOverlayContent,
  smoothnessDashboard: SmoothnessDashboardOverlayContent,
  posDrift: PosDriftOverlayContent,
  interpTuning: InterpTuningOverlayContent,
  systemHealth: SystemHealthPanel,
  sceneFxTuner: SceneFxTunerPanel,
  levelDiag: LevelDiagnosticsPanel,
  perfGraph: PerfGraphOverlayContent,
  physicsSafety: PhysicsSafetyOverlayContent,
  mpDiagnostics: MultiplayerDiagnosticsPanelContent,
  physicsDiag: PhysicsDiagnosticPanelContent,
  renderingDiag: RenderingDiagnosticPanelContent,
  authorityOverlay: NetobsAuthorityLegendContent,
  projectileTracker: ProjectileTrackerPanelContent,
  // audioDebug retired 2026-07-11 — the Audio Lab bench renders inside the
  // 🔊 gmSoundDebug card's collapsible section (sound category).
};
